import { axios } from '@/libs/api.request';
import { Response } from '@/types/request.d';
import { method } from 'lodash';
import type { ShareType } from '@/api/sys/share/dataShare';

// 成员列表接口鉴权来源类型
export type SysUserListSourceType =
    | '0' // 不进行鉴权
    | '1' // 分组成员未分组列表
    | '2' // 在线聊天
    | '3' // 客户列表-我的客户
    | '4' // 会话列表已分配
    | '5' // 大报表
    | '6' // 客户列表-有效客户
    | '7' // 客户列表-全量客户
    | '8' // 渠道分析
    | '9' // 在线聊天-分配会话
    | '10' // 客户列表-分配会话
    | '11' // 会话管理-分配会话
    | '12' // 在线时长
    | '13' // 新客户列表
    | '14'; // 客户分析

export type ChannelAuthType = '0' | '-1'; // 权限类型 -1全部 0-自定义

export interface UserListParams {
    page?: number;
    page_size?: number;
    project_id?: string;
    nickname?: string;
    source_type?: string;
    type?: ShareType;
}

export interface UserListResItem {
    id: string;
    nickname: string;
    email: string;
    type: string; // 0 超管，1 管理员，2.成员，3.定制角色（成员）
    created_time: string;
    updated_time: string;
    avatar?: string;
    online_status: '0' | '1'; // 在线状态 1-在线 0-隐身
    assign_limit: string;
    session_num?: string;
    day_session_num?: string;
    day_session_active_num?: string; // 今日接待中
    auth_key_list: string[]
    channel_auth_type?: ChannelAuthType;
    channel_auth?: {
        [index: string]: string[];
    };
    sys_user_id: string;
    online_time_switch: '0' | '1';
    online_time_timezone: string;
    online_time_configure: string;
    user_name?: string;
    user_type?: string; // ordinary 普通， advanced 高级
    identity_id?: string; // 角色id
    session_count?: number;
    online_schedule_status?: '0' | '1' // / 公共排班表开启状态 0 关闭 1 开启
    project_schedule_id?: string; // 所选排班表id
    groups?: {
        id: string;
        group_name: string;
    }[], // 用户所在分组
    active_session_num: string; // 活跃会话数
    inactive_session_num: string; // 历史会话数
    information_hidden?: '0' | '1'; // 信息隐藏
    other_information_hidden?: number[]; // 其他信息隐藏
    welcome_switch: '0' | '1';
    welcome_messages: string;
}

interface UserListRes {
    list: UserListResItem[];
    page: number;
    page_size: number;
}

export const getUserList = (params: UserListParams) => axios.request<Response<UserListRes>>({
    url: '/sys/project/user-list/get-user-list',
    method: 'get',
    params,
});

export interface UpdateUserParams {
    sys_user_id: string;
    assign_limit?: number;
    project_id?: string;
    nickname?: string;
    password?: string;
    avatar?: string;
    channel_auth_type?: ChannelAuthType;
    channel_auth?: string;
    auth_key_list?: string;
    role?: string;
    online_time_switch?: string;// 在线时间开关 1-开 0-关
    online_time_timezone?: string;// 在线时间时区
    online_time_configure?: string;// 在线时间配置
    identity_id?: string;
    online_schedule_status?: '0' | '1'; // 公共排班表开启状态 0 关闭 1 开启
    project_schedule_id?: string; // 公共排班表id
    group_ids?: string;
    information_hidden?: '0' | '1'; // 信息隐藏
    other_information_hidden?: number[]; // 其他信息隐藏
    all_information_hidden?: string; // 场景值（新旧）
}
export const updateUser = (params: UpdateUserParams) => axios.request({
    url: '/sys/project/user-list/update-user',
    method: 'post',
    data: params,
});

interface OnlineSwitchParams {
    online_status: '0' | '1' | '2'; // 在线状态 1-在线 0-离线 2-忙碌
    sys_user_id?: string;
    event_type?: '0' | '1' | '2'; // 0-手动切换 1-重连 2-切换项目
    project_id?: string;
}
export const onlineSwitch = (params: OnlineSwitchParams) => axios.request({
    url: '/sys/project/user-list/online-switch',
    method: 'post',
    data: params,
});

interface UserCountRes {
    total: string;
}

export const getUserCount = (params: UserListParams) => axios.request<Response<UserCountRes>>({
    url: '/sys/project/user-list/get-user-count',
    method: 'get',
    params,
});

interface DelUserParams {
    ids: string;
    project_id?: string;
    assign_sys_user_id?: string;
}

export const delUser = (params: DelUserParams) => axios.request({
    url: '/sys/project/user-list/del-user',
    method: 'post',
    data: params,
});

export interface AddUserParams {
    project_id: string;
    nickname: string;
    email?: string;
    role: string;
    assign_limit: number;
    avatar: string;
    channel_auth_type: ChannelAuthType;
    channel_auth: string;
    auth_key_list: string;
    user_name?: string;
    password?: string;
    identity_id: string;
    group_ids?: string;
    information_hidden?: '0' | '1'; // 信息隐藏
    other_information_hidden?: number[]; // 其他信息隐藏
    all_information_hidden?: string; // 场景值（新旧）
}

export const addUser = (params: AddUserParams) => axios.request({
    url: '/sys/project/user-list/add-user',
    method: 'post',
    data: params,
    customTips: true,
});

export const checkUserNum = (params: { project_id: string; }) => axios.request<Response<{ status: '0' | '1' }>>({
    url: '/sys/project/user-list/check-user-num',
    method: 'get',
    params,
});

/**
 * 检查邮箱
 */
export const checkEmail = (params: { project_id: string; email: string; }) => axios.request({
    url: '/sys/project/user-list/check-email',
    method: 'get',
    params,
    customTips: true,
});

interface CheckSessionRes {
    has_session: '0' | '1'; // 1-存在 0-不存在
    session_count: number;
}
/**
 * 检查会话
 */
export const checkSession = (params: { ids: string; }) => axios.request<Response<CheckSessionRes>>({
    url: '/sys/project/user-list/check-session',
    method: 'get',
    params,
});

/**
 * 获取团队成员的详细信息
 */
export const getUserInfo = (params: {
    sys_user_id: string;
}) => axios.request<Response<UserListResItem>>({
    url: '/sys/project/user-list/get-user-info',
    method: 'get',
    params,
});

interface OnlineStatusData {
    online_status: '0' | '1' | '2'; // 在线状态 1-在线 0-离线 2-忙碌
    last_online_status: '0' | '1' | '2'; // 上一次在线状态 1-在线 0-离线 2-忙碌
    online_time_switch: '0' | '1';
    can_select_status: '0' | '1' | '2'[]; // 可改在线状态 1-在线 0-离线 2-忙碌
}

/**
 * 获取用户在线状态
 */
export const onlineStatus = () => axios.request<Response<OnlineStatusData>>({
    url: '/sys/project/user-list/current-online-status',
    method: 'get',
});

type Role = '1' | '2';

/**
 * 获取角色列表
 */
export const getRoleList = () => axios.request<Response<Role[]>>({
    url: '/sys/project/user-list/get-role-list',
    method: 'get',
});

interface OnlineTimeTableItem {
    sys_user_id: string;
    nickname: string;
    type: '0' | '1';
    online_time_switch: '0' | '1';
    online_time_timezone: string;
    online_time_configure: string;
}
interface OnlineTimeTableRes {
    list: OnlineTimeTableItem[];
    page: number;
    page_size: number;
}
/**
 * 获取在线时间排班表
 */
export const getOnlineTimeTable = () => axios.request<Response<OnlineTimeTableItem[]>>({
    url: '/sys/project/user-list/get-online-time-table',
    method: 'get',
});

/**
 * 获取团队成员个人配置
 */
export const getUserConfig = (params: { type: string }) => axios.request<Response<OnlineTimeTableItem[]>>({
    url: '/sys/project/user-list/get-config',
    method: 'get',
    params,
});

export interface updateUserConfigParams {
    session_list_labels?: string,
    session_list_remark_name?: string,
    session_list_last_reply_time?: string,
    session_list_channel_name?: string;
    session_list_nickname?: string;
    system_notice_window?: string;
    session_list_phone?: string;
    session_list_email?: string;
}
/**
 * 更新团队成员个人配置
 */
export const updateUserConfig = (params: updateUserConfigParams) => axios.request({
    url: '/sys/project/user-list/update-config',
    method: 'post',
    data: params,
});

interface getCheckTeamEmailRes {
    check_status: '0' | '1'; // 0-邮箱未绑定  1-正常
}
/**
 * 检查团队成员邮箱绑定状态
 */
export const getCheckTeamEmail = () => axios.request<Response<getCheckTeamEmailRes>>({
    url: '/sys/project/user-list/check-team-email',
    method: 'get',
});

/**
 * 获取成员列表在线状态（用于轮询）
 * @param params
 */
export const getUserListOnlineStatus = (params: { sys_user_ids: string }) => axios.request<Response<{ sys_user_id: string, online_status: '0' | '1' }[]>>({
    url: '/sys/project/user-list/get-user-list-online-status',
    method: 'get',
    params,
    customTips: true,
});

export interface IdentityResItem {
    id: string;
    identity_name: string;
    is_system: string;
    identity_type: string;
    value: string;
    label: string;
}

/**
 * 获取角色列表
 */
export const getIdentityList = (params: {
    module: 'add_user' | 'update_user' | 'search_role';
    sys_user_id: string;
}) => axios.request<Response<IdentityResItem[]>>({
    url: '/sys/project/user-list/identity-list',
    method: 'get',
    params,
});

/**
 * 添加角色
 */
export const addIdentity = (params: {
    identity_type: string;
    identity_name: string;
    auth_list: string;
    description?: string;
}) => axios.request<Response<{id: string}>>({
    url: '/sys/project/user-list/add-identity',
    method: 'post',
    data: params,
});

/**
 * 编辑角色
 */
export const updateIdentity = (params: {
    id: string;
    identity_name: string;
    auth_list: string;
    identity_type?: string;
    description?: string;
}) => axios.request({
    url: '/sys/project/user-list/update-identity',
    method: 'post',
    data: params,
});

interface GetIdentityInfoRes {
    id: string;
    identity_name: string;
    auth_list: string[];
    identity_type: string;
    is_system: string;
    auth_tree_list: {
        [index: string]: any;
    },
    project_auth_list: string[];
    description?: string;
}

/**
 * 获取角色详情
 */
export const getIdentityInfo = (params: {
    id: string;
}) => axios.request<Response<GetIdentityInfoRes>>({
    url: '/sys/project/user-list/get-identity-info',
    method: 'get',
    params,
});

/**
 * 成员在线心跳
 */
export const onlineUserPing = () => axios.request({
    url: '/sys/project/user-list/online-user-ping',
    method: 'post',
    customTips: true,
});
interface BatchAssignLimitData {
    assign_limit: string;
    sys_user_ids: string;
}

/**
 * 批量修改接待数
 */
export const batchAssignLimit = (data:BatchAssignLimitData) => axios.request({
    url: '/sys/project/user-list/batch-update-assign-limit',
    method: 'post',
    data,
    customTips: true,
});

export interface GetUserChannelNumParams {
    channel: string;
    subchannel?: string; // 子渠道（whatsApp API ：0.自助，1.官方，2.代理商）
}

interface GetUserChannelNumRes {
    channel:string;
    channel_total: string;
    auth_channel_total:string;
    is_possess: '0' | '1'; // 是否添加过渠道账号
}
/**
 * 获取用户渠道数量
 */
export const getUserChannelNum = (params: GetUserChannelNumParams) => axios.request<Response<GetUserChannelNumRes>>({
    url: '/sys/project/user-list/get-user-channel-num',
    method: 'get',
    params,
});

export interface SysUserItem {
    id: string;
    nickname: string;
    sys_user_id?: string;
    email?: string;
    type?: string;
    created_time?: string;
    updated_time?: string;
    online_status?: '0' | '1' | '2'; // 上一次在线状态 1-在线 0-离线 2-忙碌
    assign_limit?: string;
    online_time_switch?: string;// 在线时间排班开关 1-开 0-关
    online_time_timezone?: string;
    online_time_configure?: string;
    information_hidden?: '0' | '1';
    other_information_hidden?: number[];
    avatar?: string;
    identity_id?: string;
}

export interface SysUserListRes {
    list: SysUserItem[];
    page: number;
    page_size: number;
}

export interface SysUserParams {
    page: number;
    page_size: number;
    nickname?: string;
    ids?: string;
    source_type?: SysUserListSourceType;
    type?: ShareType;
}

export const getMemberList = (params: SysUserParams) => axios.request<Response<SysUserListRes>>({
    url: '/sys/project/user-list/get-member-list',
    method: 'get',
    params,
});

/**
 * 获取角色是否被使用
 * @param params
 */
export const getIdentityUsage = (params: { id: string }) => axios.request<Response<{ res: number }>>({
    url: '/sys/project/user-list/get-identity-usage',
    method: 'get',
    params,
});

export interface ReplaceIdentity {
    id: string; // 被替换角色id
    replace_id: string; // 替换角色id
}

/**
 * 替换角色
 * @param data
 */
export const replaceIdentity = (data: ReplaceIdentity) => axios.request<Response<any>>({
    url: '/sys/project/user-list/replace-identity',
    method: 'post',
    data,
});

/**
 * 删除角色
 * @param data
 */
export const delIdentity = (data: { id: string }) => axios.request<Response<any>>({
    url: '/sys/project/user-list/del-identity',
    method: 'post',
    data,
});

export interface GetUnderstrapperData {
    sys_user_id: string;
    nickname: string;
}

interface GetUnderstrapperParams {
    is_include_oneself?: '0' | '1'; // 是否包含自己：1是， 0，否
}

/**
 * 话术库获取下属用户
 */
export const getUnderstrapper = (params: GetUnderstrapperParams) => axios.request<Response<GetUnderstrapperData[]>>({
    url: '/sys/project/user-list/get-understrapper-sys-user',
    method: 'get',
    params,
});

interface GetRoleIdentityListParams {
    identity_name?: string; // 搜索角色名称
    page_size: string;
    page: string;
}

interface RoleIdentityItem {
    id: string;
    identity_name: string;
    is_system: string;
    identity_type: string; // 角色类型
    description: string; // 描述
    created_time: string;
}

interface GetRoleIdentityListRes {
    list: RoleIdentityItem[];
    total: string;
    page: string;
    page_size: string;
}

/**
 * 获取角色管理下的列表
 * @param params
 */
export const getRoleIdentityList = (params: GetRoleIdentityListParams) => axios.request<Response<GetRoleIdentityListRes>>({
    url: '/sys/project/user-list/get-identity-list',
    method: 'get',
    params,
});

interface AddUserGroupData {
    group_name: string,
    description?: string,
    sys_user_ids?: string,
    id?: string, // 有id时为编辑
}

/**
 * 添加/编辑分组
 */
export const addUserGroup = (data: AddUserGroupData) => axios.request({
    url: '/sys/project/user-list/update-user-group',
    method: 'post',
    data,
});

export interface MemberUser {
    project_id: string;
    group_id: string;
    sys_user_id: string;
    nickname: string;
}

interface GetUserGroupResData {
    id: string;
    project_id: string;
    group_name: string;
    description: string;
    member_users: MemberUser[];
}

/**
 * 获取分组信息
 */
export const getUserGroup = (params: {id: string}) => axios.request<Response<GetUserGroupResData>>({
    url: '/sys/project/user-list/get-user-group',
    method: 'get',
    params,
});

export interface GroupDataType {
    id: string;
    group_name: string;
    member_count: string;
}

interface GetUserGroupListResData {
    all_count: string;
    ungrouped_count: string; // 未分组数量
    group_data: GroupDataType[];
}

/**
 * 获取分组列表
 */
export const getUserGroupTab = () => axios.request<Response<GetUserGroupListResData>>({
    url: '/sys/project/user-list/get-user-group-tab',
    method: 'get',
});

export interface GetUserGroupMemberListParams {
    id: string;
    keyword?: string; // 搜索关键词
    page?: number;
    page_size?: number;
    version?: string;
    get_session_data?: string; // 0, 1是否获取会话数据, active_session_num获取活跃会话数, inactive_session_num历史会话
    source_type?: string;
}

interface GetUserGroupMemberListResData {
    list: UserListResItem[];
    total: string;
    page: string;
    page_size: string;
}

/**
 * 获取分组成员列表
 * @param params
 * @returns
 */
export const getUserGroupMemberList = (params: GetUserGroupMemberListParams) => axios.request<Response<GetUserGroupMemberListResData>>({
    url: '/sys/project/user-list/get-user-group-member-list',
    method: 'get',
    params,
});

/**
 * 删除分组
 * @param data  - id
 * @returns
 */
export const deleteUserGroup = (data: { id: string }) => axios.request({
    url: '/sys/project/user-list/del-user-group',
    method: 'post',
    data,
});

export interface UserGroupItemType {
    id: string;
    group_name: string;
}

/**
 * 添加/编辑成员-获取分组列表
 */
export const getUserGroupList = (params = {}) => axios.request<Response<UserGroupItemType[]>>({
    url: '/sys/project/user-list/get-user-group-list',
    method: 'get',
    params,
});

interface UserListItemType {
    sys_user_id: string;
    project_id: string;
    same_members: string[];
}

/**
 * 获取当前账号同组成员
 */
export const getSameTeamList = () => axios.request<Response<UserListItemType>>({
    url: '/sys/project/user-list/get-same-group-member',
    method: 'get',
});

/**
 * 获取分组成员列表数量
 */
export const getUserGroupMemberCount = (params: GetUserGroupMemberListParams) => axios.request({
    url: '/sys/project/user-list/get-user-group-member-count',
    method: 'get',
    params,
});

type GetMemberUserSessionDataType = Omit<GetUserGroupMemberListParams, 'get_session_data' | 'version'> & {
    session_data_fields?: string;
    sys_user_ids?: string;
}

export type MemberUserSessionData = {
    sys_user_id: string;
    session_num: string;
    day_session_active_num: string;
    day_session_num: string;
    active_session_num: string;
    inactive_session_num: string;
}[]

/**
 * 获取分组成员会话数据
 */
export const getMemberUserSessionData = (params: GetMemberUserSessionDataType) => axios.request<Response<MemberUserSessionData>>({
    url: '/sys/project/user-list/get-user-session-data',
    method: 'get',
    params,
});

export interface BatchUpdateUserData {
    sys_user_ids: string;
    assign_limit?: string; // 分配上限
    channel_auth_type?: ChannelAuthType; // 渠道权限类型
    channel_auth?: string; // 渠道权限
    identity_id?: string; // 角色id
    group_ids?: string; // 分组id
    information_hidden?: '0' | '1'; // 是否开启隐私保护
    other_information_hidden?: string; // 隐私保护范围
    all_information_hidden?: string; // 场景值（新旧）
}

/**
 * 批量修改成员信息
 * @param data
 */
export const batchUpdateUser = (data: BatchUpdateUserData) => axios.request<Response<any>>({
    url: '/sys/project/user-list/batch-update-user',
    method: 'post',
    data,
});

export interface GetAllGroupAndMemberListParams {
    source_type?: SysUserListSourceType; // source_type：0.不区分来源，1.分组成员未分组列表，2.在线聊天选择用户列表， 3.客户列表我的客户选择用户列表，4.会话列表已分配选择用户列表
    get_session_data?: '0' | '1';
}

export interface GroupItem {
    id: string;
    group_name: string;
    members: UserListResItem[];
}

/**
 * 获取所有分组和成员列表
 * @param params
 * @returns {GroupItem[]} 分组成员列表
 */
export const getAllGroupAndMemberList = (params: GetAllGroupAndMemberListParams = {}) => axios.request<Response<GroupItem[]>>({
    url: '/sys/project/user-list/get-user-all-group-member',
    method: 'get',
    params,
});
